(function () {
  'use strict';

  class BundleBuilder {
    constructor(root) {
      this.root = root;
      this.requiredCount = parseInt(root.dataset.productsRequired, 10) || 3;
      this.currencySymbol = root.dataset.currencySymbol || '';

      // Parsed once: [{ id, price_cents, available }, ...] for the bundle
      // product's own variants. We pick the one whose price matches the
      // sum of whatever fragrances were selected.
      this.bundleVariants = [];
      try {
        this.bundleVariants = JSON.parse(root.dataset.bundleVariants || '[]');
      } catch (e) {
        this.bundleVariants = [];
      }

      // Cumulative selection: variantId -> { variantId, title, size, priceCents, qty }.
      // The numbered slots on the page are just a rendering of this, in
      // insertion order — they're not independent slots being assigned to.
      this.selections = new Map();

      // How many of the completed bundle to add to cart at once (e.g. 2
      // sets of "3 fragrances for £52.95" = quantity 2 of the matched variant).
      this.bundleQuantity = 1;

      // '' means the "All" tab — no collection filter applied.
      this.activeCollectionHandle = '';

      this.slotButtons = Array.from(root.querySelectorAll('[data-slot-trigger]'));
      this.drawer = root.querySelector('[data-bundle-drawer]');
      if (this.drawer && this.drawer.parentElement !== document.body) {
        document.body.appendChild(this.drawer);
      }
      this.drawerOverlay = this.drawer ? this.drawer.querySelector('[data-drawer-overlay]') : null;
      this.drawerClose = this.drawer ? this.drawer.querySelector('[data-drawer-close]') : null;
      this.drawerSearch = this.drawer ? this.drawer.querySelector('[data-drawer-search]') : null;
      this.drawerTabs = this.drawer ? Array.from(this.drawer.querySelectorAll('[data-drawer-tab]')) : [];
      this.drawerList = this.drawer ? this.drawer.querySelector('[data-drawer-list]') : null;
      this.drawerNoResults = this.drawer ? this.drawer.querySelector('[data-drawer-no-results]') : null;
      this.drawerDoneBtn = this.drawer ? this.drawer.querySelector('[data-drawer-done]') : null;
      this.drawerDoneLabel = this.drawer ? this.drawer.querySelector('[data-drawer-done-label]') : null;
      this.progressText = root.querySelector('[data-progress-text]');
      this.totalText = root.querySelector('[data-total-text]');
      this.addBundleBtn = root.querySelector('[data-add-bundle-btn]');
      this.errorMessage = root.querySelector('[data-error-message]');
      this.qtyDecreaseBtn = root.querySelector('[data-bundle-qty-decrease]');
      this.qtyIncreaseBtn = root.querySelector('[data-bundle-qty-increase]');
      this.qtyInput = root.querySelector('[data-bundle-qty-input]');

      this.bindEvents();
      this.renderAll();
    }

    bindEvents() {
      this.slotButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const variantId = btn.dataset.variantId;
          if (variantId) {
            this.decreaseItem(variantId);
          } else {
            this.openDrawer();
          }
        });
      });

      if (this.drawerOverlay) {
        this.drawerOverlay.addEventListener('click', () => this.closeDrawer());
      }
      if (this.drawerClose) {
        this.drawerClose.addEventListener('click', () => this.closeDrawer());
      }
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer && !this.drawer.hidden) {
          this.closeDrawer();
        }
      });

      if (this.drawerSearch) {
        this.drawerSearch.addEventListener('input', () => this.handleSearch());
      }

      this.drawerTabs.forEach((tabBtn) => {
        tabBtn.addEventListener('click', () => this.setActiveTab(tabBtn));
      });

      if (this.drawerList) {
        this.drawerList.addEventListener('click', (e) => this.handleStepperClick(e));
        this.drawerList.addEventListener('change', (e) => this.handleVariantSelectChange(e));
      }

      if (this.addBundleBtn) {
        this.addBundleBtn.addEventListener('click', () => this.addBundleToCart());
      }

      // Drawer's Done/Confirm button now adds the bundle to cart directly
      // instead of just closing the drawer. addBundleToCart() itself
      // closes the drawer once the add succeeds.
      if (this.drawerDoneBtn) {
        this.drawerDoneBtn.addEventListener('click', () => this.addBundleToCart());
      }

      if (this.qtyDecreaseBtn) {
        this.qtyDecreaseBtn.addEventListener('click', () => this.setBundleQuantity(this.bundleQuantity - 1));
      }
      if (this.qtyIncreaseBtn) {
        this.qtyIncreaseBtn.addEventListener('click', () => this.setBundleQuantity(this.bundleQuantity + 1));
      }
      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', () => {
          const parsed = parseInt(this.qtyInput.value, 10);
          this.setBundleQuantity(isNaN(parsed) ? 1 : parsed);
        });
      }
    }

    setBundleQuantity(value) {
      const clamped = Math.max(1, Math.floor(value) || 1);
      this.bundleQuantity = clamped;
      if (this.qtyInput) this.qtyInput.value = clamped;
      if (this.qtyDecreaseBtn) this.qtyDecreaseBtn.disabled = clamped <= 1;
      this.renderAll();
    }

    setActiveTab(tabBtn) {
      this.activeCollectionHandle = tabBtn.dataset.collectionHandle || '';
      this.drawerTabs.forEach((btn) => {
        const isActive = btn === tabBtn;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      this.handleSearch();
    }

    openDrawer() {
      if (!this.drawer) return;

      this.drawer.hidden = false;
      requestAnimationFrame(() => this.drawer.classList.add('is-open'));

      document.body.style.overflow = 'hidden';

      this.syncDrawerQuantities();

      if (this.drawerSearch) {
        this.drawerSearch.value = '';
        this.handleSearch();
        setTimeout(() => this.drawerSearch.focus(), 50);
      }
    }

    closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      const onEnd = () => {
        this.drawer.hidden = true;
        this.drawer.removeEventListener('transitionend', onEnd);
      };
      this.drawer.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
      }, 350);
    }

    itemMatchesCollection(item) {
      if (!this.activeCollectionHandle) return true;
      const collections = (item.dataset.collections || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
      return collections.indexOf(this.activeCollectionHandle) !== -1;
    }

    handleSearch() {
      if (!this.drawerList) return;
      const term = this.drawerSearch ? this.drawerSearch.value.trim().toLowerCase() : '';
      const items = this.drawerList.querySelectorAll('[data-drawer-item]');
      let visibleCount = 0;

      items.forEach((item) => {
        const searchTerm = item.dataset.searchTerm || '';
        const matchesSearch = !term || searchTerm.indexOf(term) !== -1;
        const matchesCollection = this.itemMatchesCollection(item);
        const visible = matchesSearch && matchesCollection;
        item.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      if (this.drawerNoResults) {
        this.drawerNoResults.hidden = visibleCount !== 0;
      }
    }

    handleStepperClick(e) {
      const increaseBtn = e.target.closest('[data-item-qty-increase]');
      const decreaseBtn = e.target.closest('[data-item-qty-decrease]');
      if (!increaseBtn && !decreaseBtn) return;

      const itemEl = e.target.closest('[data-drawer-item]');
      if (!itemEl) return;

      const variantId = itemEl.dataset.variantId;
      if (!variantId) return;

      if (increaseBtn) {
        if (increaseBtn.disabled) return;
        this.increaseItem(variantId, itemEl);
      } else {
        this.decreaseItem(variantId);
      }
    }

    handleVariantSelectChange(e) {
      const select = e.target.closest('[data-item-variant-select]');
      if (!select) return;

      const itemEl = select.closest('[data-drawer-item]');
      if (!itemEl) return;

      const oldVariantId = itemEl.dataset.variantId;
      const chosenOption = select.options[select.selectedIndex];
      const newVariantId = chosenOption.value;
      const newPrice = parseInt(chosenOption.dataset.price, 10) || 0;
      const newSize = chosenOption.dataset.size || '';
      const isAvailable = chosenOption.dataset.available !== 'false';

      if (oldVariantId && oldVariantId !== newVariantId && this.selections.has(oldVariantId)) {
        this.selections.delete(oldVariantId);
      }

      itemEl.dataset.variantId = newVariantId;
      itemEl.dataset.price = newPrice;
      itemEl.dataset.size = newSize;
      itemEl.dataset.available = isAvailable;
      itemEl.classList.toggle('is-sold-out', !isAvailable);

      const priceDisplay = itemEl.querySelector('[data-item-price-display]');
      if (priceDisplay) priceDisplay.textContent = this.formatCents(newPrice);

      const soldOutLabel = itemEl.querySelector('[data-item-soldout-label]');
      if (soldOutLabel) soldOutLabel.hidden = isAvailable;

      this.clearError();
      this.renderAll();
    }

    increaseItem(variantId, itemEl) {
      if (this.getSelectedCount() >= this.requiredCount) return;

      const existing = this.selections.get(variantId);
      if (existing) {
        existing.qty += 1;
      } else {
        this.selections.set(variantId, {
          variantId,
          title: itemEl.dataset.title,
          size: itemEl.dataset.size,
          priceCents: parseInt(itemEl.dataset.price, 10) || 0,
          qty: 1,
        });
      }
      this.clearError();
      this.renderAll();
    }

    decreaseItem(variantId) {
      const existing = this.selections.get(variantId);
      if (!existing) return;
      existing.qty -= 1;
      if (existing.qty <= 0) {
        this.selections.delete(variantId);
      }
      this.clearError();
      this.renderAll();
    }

    getSelectedCount() {
      let total = 0;
      this.selections.forEach((item) => { total += item.qty; });
      return total;
    }

    getSelectedTotalCents() {
      let total = 0;
      this.selections.forEach((item) => { total += item.priceCents * item.qty; });
      return total;
    }

    getDiscountRate() {
      const rates = { 3: 0.0, 4: 0.0, 6: 0.0 };
      return rates[this.requiredCount] || 0;
    }

    getDiscountedTotalCents() {
      const rawCents = this.getSelectedTotalCents();
      const rate = this.getDiscountRate();
      return Math.round(rawCents * (1 - rate));
    }

    formatCents(cents) {
      return this.currencySymbol + (cents / 100).toFixed(2);
    }

    // Finds the bundle product's variant whose price is closest to the
    // computed total, accepting a small tolerance (default 1 cent/penny)
    // to absorb rounding drift between however the total was computed
    // and however the variant price was entered in Shopify Admin.
    // Never returns a variant outside the tolerance, and never invents a
    // price — it only ever picks an existing, available variant. Ties
    // (equidistant candidates) are broken by picking the closest one
    // found first, which in practice means the lowest-priced of the two
    // since bundleVariants is generally price-sorted; this only matters
    // in the pathological case of duplicate-priced variants.
    findMatchingBundleVariant(totalCents, toleranceCents) {
      const tolerance = typeof toleranceCents === 'number' ? toleranceCents : 1;

      // First try an exact match — cheap, and avoids any ambiguity when
      // the price lines up perfectly (the common case).
      const exact = this.bundleVariants.find(
        (v) => v.price_cents === totalCents && v.available !== false
      );
      if (exact) return exact;

      // Fall back to the closest available variant within tolerance.
      let best = null;
      let bestDiff = Infinity;
      this.bundleVariants.forEach((v) => {
        if (v.available === false) return;
        const diff = Math.abs(v.price_cents - totalCents);
        if (diff <= tolerance && diff < bestDiff) {
          best = v;
          bestDiff = diff;
        }
      });
      return best;
    }

    showError(message) {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
    }

    clearError() {
      if (!this.errorMessage) return;
      this.errorMessage.hidden = true;
      this.errorMessage.textContent = '';
    }

    syncDrawerQuantities() {
      if (!this.drawerList) return;
      const atCap = this.getSelectedCount() >= this.requiredCount;

      this.drawerList.querySelectorAll('[data-drawer-item]').forEach((itemEl) => {
        const variantId = itemEl.dataset.variantId;
        const selected = this.selections.get(variantId);
        const qty = selected ? selected.qty : 0;
        const valueEl = itemEl.querySelector('[data-item-qty-value]');
        const increaseBtn = itemEl.querySelector('[data-item-qty-increase]');
        const decreaseBtn = itemEl.querySelector('[data-item-qty-decrease]');
        const isAvailable = itemEl.dataset.available !== 'false';

        if (valueEl) valueEl.textContent = qty;
        if (decreaseBtn) decreaseBtn.disabled = qty <= 0;
        if (increaseBtn) increaseBtn.disabled = !isAvailable || atCap;

        // Highlight the row once at least one unit of it is selected.
        itemEl.classList.toggle('is-selected', qty > 0);
      });
    }

    getFlattenedSelections() {
      const flat = [];
      this.selections.forEach((item) => {
        for (let i = 0; i < item.qty; i += 1) {
          flat.push(item);
        }
      });
      return flat;
    }

    renderSlots() {
      const flat = this.getFlattenedSelections();

      this.slotButtons.forEach((btn) => {
        const index = parseInt(btn.dataset.slotIndex, 10);
        const item = flat[index - 1];
        const label = btn.querySelector('[data-slot-label]');

        if (item) {
          btn.classList.add('is-filled');
          btn.dataset.variantId = item.variantId;
          btn.setAttribute('aria-label', 'Remove ' + item.title + ' (' + item.size + ')');
          if (label) label.textContent = item.title + ' (' + item.size + ')';
        } else {
          btn.classList.remove('is-filled');
          delete btn.dataset.variantId;
          btn.removeAttribute('aria-label');
          if (label) label.textContent = 'Choose Option ' + index;
        }
      });
    }

    renderAll() {
      this.renderSlots();
      this.syncDrawerQuantities();

      const selectedCount = this.getSelectedCount();

      if (this.progressText) {
        this.progressText.textContent = selectedCount + ' / ' + this.requiredCount + ' Selected';
      }

      const isComplete = selectedCount === this.requiredCount;
      const rawCents = this.getSelectedTotalCents() * this.bundleQuantity;
      const discountedCents = this.getDiscountedTotalCents() * this.bundleQuantity;
      const rate = this.getDiscountRate();

      if (this.totalText) {
        if (isComplete) {
          if (rate > 0) {
            this.totalText.innerHTML =
              'Total: <s class="bundle-builder__total-original">' + this.formatCents(rawCents) + '</s> ' +
              '<span class="bundle-builder__total-discounted">' + this.formatCents(discountedCents) + '</span> ' +
              '<span class="bundle-builder__total-savings">(' + Math.round(rate * 100) + '% off)</span>';
          } else {
            this.totalText.textContent = 'Total: ' + this.formatCents(discountedCents);
          }
          this.totalText.hidden = false;
        } else {
          this.totalText.hidden = true;
        }
      }

      if (this.addBundleBtn) {
        this.addBundleBtn.disabled = !isComplete;
        this.addBundleBtn.textContent = isComplete
          ? 'Add Bundle - ' + this.formatCents(discountedCents)
          : 'Add Bundle';
      }

      if (this.drawerDoneBtn) {
        this.drawerDoneBtn.hidden = !isComplete;
        this.drawerDoneBtn.disabled = false;
      }
      if (this.drawerDoneLabel && isComplete) {
        this.drawerDoneLabel.textContent = 'Add Bundle - ' + this.formatCents(discountedCents);
      }
    }

    async addBundleToCart() {
      const selectedCount = this.getSelectedCount();
      if (selectedCount !== this.requiredCount) return;

      const rawCents = this.getSelectedTotalCents();
      const discountedCents = this.getDiscountedTotalCents();
      const matchedVariant = this.findMatchingBundleVariant(discountedCents);

      if (!matchedVariant) {
        this.showError(
          'This combination totals ' + this.formatCents(rawCents) +
          ', which discounts to ' + this.formatCents(discountedCents) +
          ', but the bundle product has no variant near that price yet. ' +
          'Add a ' + this.formatCents(discountedCents) + ' variant to the bundle product in Shopify Admin.'
        );
        return;
      }

      // Disable both possible triggers (page button + drawer Done button)
      // while the request is in flight, so a double-click from either
      // spot can't fire two add requests.
      if (this.addBundleBtn) {
        this.addBundleBtn.disabled = true;
        this.addBundleBtn.textContent = 'Adding...';
      }
      if (this.drawerDoneBtn) {
        this.drawerDoneBtn.disabled = true;
      }
      if (this.drawerDoneLabel) {
        this.drawerDoneLabel.textContent = 'Adding...';
      }

      // One property row per distinct fragrance, with its quantity folded
      // in (e.g. "Rose Oud (5ml) x2") rather than one row per slot.
      const properties = {};
      let n = 1;
      this.selections.forEach((item) => {
        properties['Fragrance ' + n] = item.title + ' (' + item.size + ') x' + item.qty;
        properties['_Variant ID ' + n] = item.variantId;
        n += 1;
      });

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                // Always the real, matched variant's own id — the cart is
                // charged whatever that variant costs in Shopify, never
                // the locally-computed discountedCents figure.
                id: matchedVariant.id,
                quantity: this.bundleQuantity,
                properties: properties,
              },
            ],
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || 'Could not add bundle to cart.');
        }

        this.resetSelection();
        this.closeDrawer();
        await this.openCartDrawer();
      } catch (err) {
        this.showError(err.message || 'Something went wrong adding the bundle.');
      } finally {
        this.renderAll();
      }
    }

    resetSelection() {
      this.selections.clear();
      this.setBundleQuantity(1);
      this.renderAll();
    }

    async openCartDrawer() {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      const cartDrawer = document.querySelector('cart-drawer, #CartDrawer, cart-notification');
      if (cartDrawer) {
        try {
          const sectionsResponse = await fetch('/cart?sections=cart-drawer,cart-icon-bubble');
          if (sectionsResponse.ok) {
            const sections = await sectionsResponse.json();
            document.dispatchEvent(
              new CustomEvent('cart:updated', { bubbles: true, detail: { sections: sections } })
            );
          }
        } catch (e) {
          // Section fetch failed silently; cart:refresh listeners can still handle it.
        }

        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          cartDrawer.classList.add('active', 'is-open', 'drawer--open');
        }
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-bundle-builder]').forEach((root) => {
      new BundleBuilder(root);
    });
  });
})();